import { z } from "zod";

export type Operator =
  | "eq"
  | "neq"
  | "contains"
  | "not_contains"
  | "in"
  | "not_in"
  | "exists"
  | "not_exists";

export type ConditionOperator = "AND" | "OR";

const assignedToSchema = z.object({
  type: z.string(),
  user_id: z.string().optional(),
  expression: z.string().optional(),
  params: z
    .object({
      source_role: z.string(),
      source_rank: z.number(),
    })
    .optional(),
});

const ownerAssignmentSchema = z.object({
  role: z.string(),
  rank: z.number(),
  assigned_to: assignedToSchema,
});

const vacancyHandlingSchema = z.object({
  when: z.object({
    owner_type: z.string(),
    rank: z.number(),
    condition: z.string(),
  }),
  then: z.object({
    action: z.string(),
  }),
});

const actionPayloadSchema = z.object({
  type: z.string(),
  value: z.array(ownerAssignmentSchema).optional(),
  payload: z.array(vacancyHandlingSchema),
});

const actionSchema = z.object({
  type: z.string(),
  payload: z.array(actionPayloadSchema),
});

const conditionObjectSchema = z.object({
  field: z.string(),
  operator: z.string(),
  value: z.string(),
});

const conditionsSchema = z.object({
  operator: z.string(),
  conditions: z.array(z.union([conditionObjectSchema, z.string()])),
});

export const agentPolicySchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  domain: z.string(),
  isActive: z.boolean(),
  isCustom: z.boolean(),
  batchModeEnabled: z.boolean(),
  triggerType: z.string(),
  conditions: conditionsSchema,
  actions: z.array(actionSchema),
  createdBy: z.string(),
  lastUpdatedBy: z.string(),
  lastUsedTs: z.number(),
  createdTs: z.number(),
  updatedTs: z.number(),
});

const paginationSchema = z.object({
  totalCount: z.number(),
  limit: z.number(),
  nextToken: z.string(),
});

export const agentPolicyResponseSchema = z.object({
  data: z.array(agentPolicySchema),
  pagination: paginationSchema,
});

export type AgentPolicyResponse = z.infer<typeof agentPolicyResponseSchema>;
export type AgentPolicy = z.infer<typeof agentPolicySchema>;
export type AgentPolicyCondition = z.infer<typeof conditionsSchema>;
export type AgentPolicyConditionObject = z.infer<typeof conditionObjectSchema>;
export type OwnerAssignment = z.infer<typeof ownerAssignmentSchema>;
export type PolicyAction = z.infer<typeof actionSchema>;
